import {
  OrderSearchErrors,
  TimeFrame,
} from "@redotech/customer-portal-sdk/rpc/schema/get-orders-from-search";
import { Currency } from "@redotech/money/iso4217/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import {
  CURRENCY_FORMAT,
  ReturnItemParams,
  shopifyLineItemToReturnItemParams,
} from "@redotech/redo-customer-portal-shared/util";
import { isRedoLineItem, Order } from "@redotech/redo-model/order";
import { SalesChannels } from "@redotech/redo-model/sales-channels";
import {
  CompleteOrderFulfillmentStatus,
  LineItem,
} from "@redotech/redo-model/shopify-order";
import { PurchaseChannel } from "@redotech/redo-model/warranties";
import { WarrantyRegistration } from "@redotech/redo-model/warranty-registration";
import { RedoBadgeColor } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { ClientError } from "@redotech/rpc/errors";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { SettingsContext } from "../../../contexts/settings";
import { UserContext } from "../../../contexts/user";
import { orderSearchErrorToText } from "../../../login/dynamic-text-utils";
import { useOrderReturns } from "./use-order-returns";

export enum OrderTimeOptionId {
  LAST_6_MONTHS = "last-6-months",
  LAST_YEAR = "last-year",
  LAST_2_YEARS = "last-2-years",
}

const ORDER_TIME_OPTIONS: Record<
  OrderTimeOptionId,
  RedoListItem<Temporal.Duration>
> = {
  [OrderTimeOptionId.LAST_6_MONTHS]: {
    id: OrderTimeOptionId.LAST_6_MONTHS,
    text: "Last 6 months",
    value: Temporal.Duration.from({ months: 6 }),
    type: "text",
  },
  [OrderTimeOptionId.LAST_YEAR]: {
    id: OrderTimeOptionId.LAST_YEAR,
    text: "Last year",
    value: Temporal.Duration.from({ years: 1 }),
    type: "text",
  },
  [OrderTimeOptionId.LAST_2_YEARS]: {
    id: OrderTimeOptionId.LAST_2_YEARS,
    text: "Last 2 years",
    value: Temporal.Duration.from({ years: 2 }),
    type: "text",
  },
};

// Number of orders to load per page/batch
const ORDERS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;

export function useOrders({ registerOnly }: { registerOnly: boolean }) {
  const settings = useContext(SettingsContext);
  const user = useContext(UserContext);
  const client = useRequiredContext(RpcClientContext);
  const navigate = useNavigate();

  const [searchString, setSearchString] = useState("");
  const [debouncedSearchString, setDebouncedSearchString] = useState("");
  const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [selectedOrderTimeOptionId, setSelectedOrderTimeOptionId] =
    useState<OrderTimeOptionId>(OrderTimeOptionId.LAST_6_MONTHS);

  // State for pagination
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [displayedOrders, setDisplayedOrders] = useState<Order[]>([]);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [loadedPage, setLoadedPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Debounce search input to avoid excessive API calls
  useEffect(() => {
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearchString(searchString);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, [searchString]);

  // Convert OrderTimeOptionId to timeFrame string for getOrdersFromSearch
  const getTimeFrameString = (
    optionId: OrderTimeOptionId,
  ): "6m" | "1y" | "2y" => {
    switch (optionId) {
      case OrderTimeOptionId.LAST_6_MONTHS:
        return TimeFrame.SIX_MONTHS;
      case OrderTimeOptionId.LAST_YEAR:
        return TimeFrame.ONE_YEAR;
      case OrderTimeOptionId.LAST_2_YEARS:
        return TimeFrame.TWO_YEARS;
      default:
        return TimeFrame.SIX_MONTHS;
    }
  };

  // If the search string is empty, AND no orders are found, show the login again button
  const shouldShowLoginAgainButton = !searchString;

  const initialOrdersLoad = useLoad(async () => {
    if (!user || !settings || !client) {
      return undefined;
    }

    try {
      // Only show loading state on initial load, not on filter changes
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }

      // Reset pagination state when fetching new orders
      setLoadedPage(1);
      setHasMoreOrders(true);

      const result = await client.getOrdersFromSearch({
        searchTerm: debouncedSearchString || null,
        timeFrame: getTimeFrameString(selectedOrderTimeOptionId) as TimeFrame,
      });

      // Use double type assertion through unknown to safely convert between incompatible types
      const orders = result.orders as unknown as Order[];

      // Store all orders but only display the first page
      setAllOrders(orders);
      setHasMoreOrders(orders.length > ORDERS_PER_PAGE);

      // Set displayed orders to first page
      setDisplayedOrders(orders.slice(0, ORDERS_PER_PAGE));

      return { orders, errorMessage: null };
    } catch (error) {
      let errorMessage = "Error fetching orders";
      if (error instanceof ClientError) {
        const rpcError = error as ClientError<OrderSearchErrors>;
        if (rpcError.code) {
          errorMessage = orderSearchErrorToText[rpcError.code];
        }
      }
      console.error("Error fetching orders:", error);
      setAllOrders([]);
      setDisplayedOrders([]);
      setHasMoreOrders(false);
      return { orders: [], errorMessage };
    }
  }, [
    user,
    settings,
    client,
    debouncedSearchString,
    selectedOrderTimeOptionId,
  ]);

  // Hook for loading more orders on demand
  const [loadMoreState, triggerLoadMore] = useTriggerLoad(async () => {
    if (!hasMoreOrders || loadedPage * ORDERS_PER_PAGE >= allOrders.length) {
      setHasMoreOrders(false);
      return undefined;
    }

    const nextPage = loadedPage + 1;
    const startIndex = (nextPage - 1) * ORDERS_PER_PAGE;
    const endIndex = nextPage * ORDERS_PER_PAGE;
    const nextBatch = allOrders.slice(startIndex, endIndex);

    setLoadedPage(nextPage);
    setDisplayedOrders((prev) => [...prev, ...nextBatch]);
    setHasMoreOrders(endIndex < allOrders.length);

    return nextBatch;
  });

  const registrationsLoad = useLoad(async () => {
    if (!user || !settings) {
      return undefined;
    }
    const { registrations } = await client.getRegistrations({});
    const registrationsByOrderId = registrations.reduce(
      (acc, registration) => {
        if (
          registration.source &&
          registration.source.purchaseChannel === PurchaseChannel.ONLINE
        ) {
          acc[registration.source.orderId] = [
            ...(acc[registration.source.orderId] ?? []),
            registration,
          ];
        }
        return acc;
      },
      {} as Record<string, WarrantyRegistration[]>,
    );
    return registrationsByOrderId;
  }, [user, settings]);

  const filteredOrders = useMemo(() => {
    if (!displayedOrders.length) {
      return [];
    }

    // Only filter by fulfillment status if in registerOnly mode
    if (registerOnly) {
      return displayedOrders.filter((order) => {
        const isFulfilled =
          order.shopify.fulfillment_status &&
          CompleteOrderFulfillmentStatus.fromValue(
            order.shopify.fulfillment_status,
          ) === CompleteOrderFulfillmentStatus.FULFILLED;

        return isFulfilled;
      });
    }

    // If not in registerOnly mode, return all orders
    return displayedOrders;
  }, [displayedOrders, registerOnly]);

  const ordersLoading = useMemo(() => {
    return initialOrdersLoad.pending || loadMoreState.pending;
  }, [initialOrdersLoad.pending, loadMoreState.pending]);

  // Update references to use the new response structure
  useEffect(() => {
    if (
      initialOrdersLoad.value &&
      Array.isArray(initialOrdersLoad.value.orders)
    ) {
      // Set all orders from the new response structure
      setAllOrders(initialOrdersLoad.value.orders);
      // Set displayed orders to first page
      setDisplayedOrders(
        initialOrdersLoad.value.orders.slice(0, ORDERS_PER_PAGE),
      );
      // Update has more indicator
      setHasMoreOrders(initialOrdersLoad.value.orders.length > ORDERS_PER_PAGE);
    }
  }, [initialOrdersLoad.value]);

  const totalOrders = useMemo(() => {
    return allOrders.length;
  }, [allOrders]);

  const totalSearchResults = useMemo(() => {
    if (!searchString) {
      return 0;
    }
    return allOrders.length;
  }, [allOrders.length, searchString]);

  const registrationsForOrders = useMemo(() => {
    if (!registrationsLoad.value) {
      return {};
    }
    return registrationsLoad.value;
  }, [registrationsLoad.value]);

  // Extract error message from the initialOrdersLoad result
  const ordersFetchError = useMemo(() => {
    return initialOrdersLoad.value?.errorMessage || null;
  }, [initialOrdersLoad.value]);

  const handleRetailRegisterClick = useHandler(() => {
    navigate("/returns-portal/register/retail");
  });

  const handleOptionSelected = useHandler(
    (args: { item: RedoListItem<Temporal.Duration> }) => {
      setSelectedOrderTimeOptionId(args.item.id as OrderTimeOptionId);
    },
  );

  const handleLoadMore = useCallback(() => {
    if (!ordersLoading && hasMoreOrders) {
      triggerLoadMore();
    }
  }, [ordersLoading, hasMoreOrders, triggerLoadMore]);

  useEffect(() => {
    // If registration is disabled, or we are not showing only showing the registration action, navigate to the old orders page
    // Once this component is also the orders page, this should be reworked
    if (!settings?.warranties?.registrationEnabled || !registerOnly) {
      navigate("/returns-portal/orders");
    }
  }, [settings?.warranties?.registrationEnabled, navigate, registerOnly]);

  return {
    shouldShowLoginAgainButton,
    ordersLoading,
    totalOrders,
    totalSearchResults,
    filteredOrders,
    isMobile: useScreenType() === ScreenType.MOBILE,
    searchString,
    timeFrameOptions: Object.values(ORDER_TIME_OPTIONS),
    selectedTimeFrameOption: ORDER_TIME_OPTIONS[selectedOrderTimeOptionId],
    hasMoreOrders,
    loadMoreOrders: handleLoadMore,
    findRegistrationsByOrderId: (orderId: string): WarrantyRegistration[] =>
      registrationsForOrders[orderId] ?? [],
    setSearchString,
    handleOptionSelected,
    handleRetailRegisterClick,
    handleDifferentEmailClick: useHandler(() => {
      navigate("/returns-portal/logout");
    }),
    text: { header: "Orders", searchPlaceholder: "Search orders..." },
    ordersError: ordersFetchError,
  };
}

export function useOrderCard({ order }: { order: Order }) {
  const client = useRequiredContext(RpcClientContext);
  const settings = useContext(SettingsContext);
  const navigate = useNavigate();
  const orderReturns = useOrderReturns(order);
  const {
    returns,
    isLoading: returnsLoading,
    hasReturnableItems,
    getReturnableQuantity,
  } = orderReturns;

  const handleRegisterClick = useHandler(() => {
    navigate(`/returns-portal/orders/${order._id}/register`);
  });

  const handleViewOrderDetailsClick = useHandler(() => {
    navigate(`/returns-portal/orders/${order._id}`);
  });

  const handleViewReturnDetailsClick = useHandler((returnId?: string) => {
    // If a specific returnId was provided, navigate to that return
    if (returnId) {
      navigate(`/returns-portal/returns/${returnId}`);
      return;
    } else {
      // Fallback to order page if no return ID is found
      navigate(`/returns-portal/orders/${order._id}`);
    }
  });

  const handleStartReturnClick = useHandler(() => {
    navigate(`/returns-portal/orders/${order._id}/return`);
  });

  const handleStartClaimClick = useHandler(() => {
    navigate(`/returns-portal/orders/${order._id}/claim`);
  });

  const handleStartWarrantyClaimClick = useHandler(() => {
    navigate(`/returns-portal/orders/${order._id}/warranty-claim`);
  });

  // Get registration data from a single RPC call
  const registrationEligibilityLoad = useLoad(async () => {
    const eligibilityData = await client.getOrderRegistrationEligibility({
      orderId: order._id,
    });

    // Convert the array of eligible line item IDs to a Record for easier lookup
    const lineItemIds = order.shopify.line_items.map((lineItem) =>
      String(lineItem.id),
    );
    const eligibilityByLineItemId: Record<string, boolean> = Object.fromEntries(
      lineItemIds.map((lineItemId) => [
        lineItemId,
        eligibilityData.eligibleLineItemIds.includes(lineItemId),
      ]),
    );

    return {
      registrationsByLineItemId: eligibilityData.registrationsByLineItemId,
      eligibilityByLineItemId,
      canBeRegistered: eligibilityData.canBeRegistered,
    };
  }, [order._id, client]);

  const totalRegistrationsByLineItemId: Record<string, number> =
    registrationEligibilityLoad.value?.registrationsByLineItemId || {};
  const registrationEligibilityByLineItemId =
    registrationEligibilityLoad.value?.eligibilityByLineItemId || {};
  const registrationEligibilityHasError = registrationEligibilityLoad.error;
  const hideRegisterButton =
    !registrationEligibilityLoad.value?.canBeRegistered;
  const shouldDisableButtons =
    !hasReturnableItems || !settings?.returns?.enableCustomerPortal;
  const filteredLineItems = useMemo(() => {
    return order.shopify.line_items.filter(
      (lineItem) => !isRedoLineItem(lineItem),
    );
  }, [order.shopify.line_items]);

  // Format the date for Temporal API
  const formattedOrderDate = useMemo(() => {
    try {
      // Ensure we're working with an ISO string
      const dateStr =
        typeof order.createdAt === "string"
          ? order.createdAt
          : new Date(order.createdAt).toISOString();

      return Temporal.Instant.from(dateStr).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      // Fallback to simpler date formatting
      return new Date(order.createdAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }, [order.createdAt]);

  return {
    orderName: order.shopify.name,
    formattedOrderDate,
    orderSourceDisplayName: SalesChannels.find((channel) =>
      channel.sourceNames.includes(order.shopify.source_name),
    )?.displayName,
    orderItems: filteredLineItems,
    hideRegisterButton,
    returns,
    returnsLoading,
    totalReturns: returns.length,
    shouldDisableButtons,
    getReturnableQuantity,
    findTotalRegistrationsByLineItemId: (lineItemId: string): number =>
      totalRegistrationsByLineItemId[lineItemId] ?? 0,
    isLineItemEligibleForRegistration: (lineItemId: string): boolean =>
      registrationEligibilityHasError
        ? false
        : registrationEligibilityByLineItemId?.[lineItemId],
    handleRegisterClick,
    handleViewOrderDetailsClick,
    handleViewReturnDetailsClick,
    handleStartReturnClick,
    handleStartClaimClick,
    handleStartWarrantyClaimClick,
    text: {
      registerButton: "Register a product",
      viewOrderDetails: "View order details",
      returnsLabel: "Returns:",
      startReturnButton: "Start a return",
      startClaimButton: "File a shipping claim",
      startWarrantyClaimButton: "File a warranty claim",
    },
    getReturnCompletionMessage: orderReturns.getReturnCompletionMessage,
    getReturnHeaderText: orderReturns.getReturnHeaderText,
    getReturnedQuantityForReturn: orderReturns.getReturnedQuantityForReturn,
    separateLineItems: orderReturns.separateLineItems,
    getReturnsByReturnId: orderReturns.getReturnsByReturnId,
    getReturnedItemsByReturnId: orderReturns.getReturnedItemsByReturnId,
  };
}

type RegistrationStatus = { displayText: string; color: RedoBadgeColor };

const screenTypeToRegistrationStatusText = {
  [ScreenType.MOBILE]: {
    registered: "Registered",
    partiallyRegistered: " registered",
    notRegistered: "Not registered",
  },
  [ScreenType.DESKTOP]: {
    registered: "Yes",
    partiallyRegistered: " registered",
    notRegistered: "No",
  },
};

function getRegistrationStatus(
  lineItem: LineItem,
  totalRegistrations: number,
  screenType: ScreenType,
  isEligibleForRegistration: boolean | undefined,
): RegistrationStatus | undefined {
  if (isEligibleForRegistration === undefined) {
    return undefined;
  }

  if (isEligibleForRegistration === false) {
    return { displayText: "N/A", color: "gray" };
  }
  // If all items are registered, return registered
  if (totalRegistrations === lineItem.quantity) {
    return {
      displayText: screenTypeToRegistrationStatusText[screenType].registered,
      color: "success",
    };
  }

  // If some items are registered, return partially registered
  if (totalRegistrations > 0) {
    return {
      displayText: `${totalRegistrations}${screenTypeToRegistrationStatusText[screenType].partiallyRegistered}`,
      color: "warning",
    };
  }

  // If no items are registered, return not registered
  return {
    displayText: screenTypeToRegistrationStatusText[screenType].notRegistered,
    color: "gray",
  };
}

type LineItemHeaderRow = {
  isHeader: true;
  text: { item: string; quantity: string; price: string; registered: string };
};

type LineItemRow = {
  isHeader: false;
  itemDetails: ReturnItemParams | undefined;
  registrationStatus: RegistrationStatus | undefined;
  formattedPrice: string;
  formattedQuantity: string;
};

export function useLineItemRow({
  lineItem,
  totalRegistrations,
  isEligibleForRegistration,
}: {
  lineItem?: LineItem;
  totalRegistrations?: number;
  isEligibleForRegistration?: boolean;
}): LineItemRow | LineItemHeaderRow {
  const screenType = useScreenType();
  const isHeader = !lineItem;
  if (isHeader) {
    return {
      isHeader: true,
      text: {
        item: "Item",
        quantity: "Quantity",
        price: "Price",
        registered: "Registered",
      },
    };
  }

  if (!lineItem.image || !lineItem.image.src) {
    lineItem.image = {
      src: "https://assets.getredo.com/product-image.png",
      alt: "No image available",
    } as any;
  }

  // Ensure we have a valid currency code with a fallback to USD
  const currencyCode: Currency =
    (lineItem.price_set?.presentment_money?.currency_code as Currency) ||
    Currency.USD;

  // Format price with error handling
  let formattedPrice = "";
  try {
    formattedPrice = CURRENCY_FORMAT(currencyCode).format(
      Number(lineItem.price),
    );
  } catch (error) {
    console.error("Error formatting price:", error);
    // Fallback to basic formatting
    formattedPrice = `$${Number(lineItem.price).toFixed(2)}`;
  }

  return {
    isHeader: false,
    itemDetails: shopifyLineItemToReturnItemParams(lineItem),
    registrationStatus: getRegistrationStatus(
      lineItem,
      totalRegistrations ?? 0,
      screenType,
      isEligibleForRegistration,
    ),
    formattedPrice,
    formattedQuantity: lineItem.quantity.toString(),
  };
}
